import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock3, Frame, ImagePlus, Loader2, Send, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextLite } from "@/components/shared/RichTextLite";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/admin/Fields";
import { ImageDisplayDialog } from "@/components/admin/ImageDisplayDialog";
import { errorMessage, imgUrl, orgContent, orgPropose, orgUploadImage, orgWithdraw } from "@/lib/api";
import { displaySummary, isDefaultDisplay } from "@/lib/display";
import { compressImage } from "@/lib/media";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const idOf = (url = "") => (String(url).match(/\/api\/images\/([0-9a-f-]{36})/i) || [])[1] || null;

const Field = ({ label, hint, count, children, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3">
      <label className="text-[13px] font-medium">{label}</label>
      {count && <span className={cn("tabular text-[12px]", count.n > count.max ? "text-destructive" : "text-muted-foreground")}>{count.n}/{count.max}</span>}
    </div>
    {hint && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

/** What the board decided last time, or that a proposal is waiting – always with the date, never a bare «OK». */
const StatusBanner = ({ pending, last }) => {
  if (pending) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-sol/40 bg-[hsl(var(--status-pending-bg))] px-4 py-3 text-[14px]" data-testid="proposal-status-pending">
        <span className="inline-flex items-center gap-2 font-medium"><Clock3 className="h-4 w-4 text-sol" /> Forslaget ligg til vurdering hos styret</span>
        <span className="text-muted-foreground">Sendt {fmtDateTime(pending.updated_at)}{pending.revision > 1 ? ` · revisjon ${pending.revision}` : ""}</span>
      </div>
    );
  }
  if (last?.status === "approved") {
    return (
      <div className="rounded-[14px] border border-emerald-600/30 bg-emerald-50 px-4 py-3 text-[14px]" data-testid="proposal-status-approved">
        <span className="inline-flex items-center gap-2 font-medium text-emerald-800"><Check className="h-4 w-4" /> Godkjent av styret {fmtDateTime(last.decided_at)}</span>
        {last.board_note && <p className="mt-1 text-emerald-900/80">«{last.board_note}»</p>}
      </div>
    );
  }
  if (last?.status === "rejected") {
    return (
      <div className="rounded-[14px] border border-rose-600/30 bg-rose-50 px-4 py-3 text-[14px]" data-testid="proposal-status-rejected">
        <span className="inline-flex items-center gap-2 font-medium text-rose-800"><X className="h-4 w-4" /> Ikkje godkjent {fmtDateTime(last.decided_at)}</span>
        {last.board_note && <p className="mt-1 text-rose-900/80">Styret skriv: «{last.board_note}»</p>}
        <p className="mt-1 text-rose-900/70">Du kan rette og sende eit nytt forslag under.</p>
      </div>
    );
  }
  return null;
};

const ImageTile = ({ im, onChange, onRemove, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="overflow-hidden rounded-[14px] border border-border bg-card" data-testid={testId}>
      <div className="relative aspect-[4/3] bg-secondary">
        <img src={imgUrl(im.url, 480)} alt="" className="h-full w-full object-contain" />
        <button type="button" onClick={onRemove} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-ink/60 text-paper backdrop-blur-md transition-colors hover:bg-destructive" aria-label="Fjern biletet" data-testid={`${testId}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="space-y-2.5 p-3">
        <button type="button" onClick={() => setOpen(true)} className={cn("flex h-9 w-full items-center justify-between gap-2 rounded-[8px] border px-2.5 text-[13px] transition-colors hover:bg-background", isDefaultDisplay(im.display) ? "border-border bg-card" : "border-foreground/40 bg-background")} data-testid={`${testId}-display`}>
          <span className="inline-flex min-w-0 items-center gap-1.5"><Frame className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{displaySummary(im.display)}</span></span>
          <span className="t-mono-sm shrink-0 text-muted-foreground">Vising</span>
        </button>
        <Input value={im.credit || ""} onChange={(e) => onChange({ ...im, credit: e.target.value })} placeholder="Foto: namn på fotograf" maxLength={120} className="h-9 bg-background text-[13.5px]" data-testid={`${testId}-credit`} />
        <label className="flex items-start gap-2 text-[12.5px] leading-snug">
          <Checkbox checked={!!im.rights_confirmed} onCheckedChange={(v) => onChange({ ...im, rights_confirmed: !!v })} className="mt-0.5" data-testid={`${testId}-rights`} />
          <span>Vi har rett til å bruke biletet til omtale av arrangementet på nettsida til kulturhuset.</span>
        </label>
      </div>
      <ImageDisplayDialog open={open} onOpenChange={setOpen} src={im.url} value={im.display} onApply={(v) => onChange({ ...im, display: v })} testId={`${testId}-display-dialog`} />
    </li>
  );
};

const emptyForm = { summary: "", description: "", description_html: "", external_url: "", contact_email: "", contact_phone: "", note: "", images: [] };
const fromProposal = (p) => ({ summary: p.fields?.summary || "", description: p.fields?.description || "", description_html: p.fields?.description_html || "", external_url: p.fields?.external_url || "", contact_email: p.fields?.contact_email || "", contact_phone: p.fields?.contact_phone || "", note: p.note || "", images: (p.images || []).map((im) => ({ ...im })) });
const fromLive = (live) => ({ ...emptyForm, summary: live?.summary || "", description: live?.description || "", description_html: live?.description_html || "", external_url: live?.external_url || "", contact_email: live?.contact_email || "", contact_phone: live?.contact_phone || "" });

/**
 * «Om arrangementet» on the organizer page: the organizer writes the public text, adds up to three pictures with credit and
 * rights confirmation, a link and public contact details – and sends it to the board. Nothing goes live before approval.
 */
export const ProposalEditor = ({ token, eventId }) => {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["org-content", token, eventId], queryFn: () => orgContent(token, eventId), retry: false });
  const [form, setForm] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (data && form === null) setForm(data.pending ? fromProposal(data.pending) : fromLive(data.live)); }, [data, form]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["org-content", token, eventId] });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = useMutation({
    mutationFn: () => orgPropose(token, eventId, {
      summary: form.summary || null, description: form.description || null, description_html: form.description_html || null, external_url: form.external_url || null,
      contact_email: form.contact_email || null, contact_phone: form.contact_phone || null, note: form.note || null,
      images: form.images.map((im) => ({ id: im.id || idOf(im.url), credit: im.credit || null, rights_confirmed: !!im.rights_confirmed, display: im.display || null })),
    }),
    onSuccess: (d) => { toast.success(d.pending?.revision > 1 ? "Forslaget er oppdatert og sendt til styret" : "Forslaget er sendt til styret"); setForm(fromProposal(d.pending)); invalidate(); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikkje sende forslaget")),
  });
  const withdraw = useMutation({
    mutationFn: () => orgWithdraw(token, eventId),
    onSuccess: () => { toast.success("Forslaget er trekt – utkastet ditt står her til du vil sende det att"); setWithdrawOpen(false); invalidate(); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const addFiles = async (files) => {
    const room = (data?.limits?.images || 3) - form.images.length;
    const list = Array.from(files || []).slice(0, Math.max(0, room));
    if (!list.length) { toast.warning(`Maks ${data?.limits?.images || 3} bilete`); return; }
    setUploading(true);
    for (const f of list) {
      try {
        const up = await orgUploadImage(token, await compressImage(f));
        setForm((cur) => ({ ...cur, images: [...cur.images, { id: up.id, url: up.url, credit: "", rights_confirmed: false }] }));
      } catch (e) { toast.error(`${f.name}: ${errorMessage(e, "opplasting feila")}`); }
    }
    setUploading(false);
  };

  if (isLoading || !form) return <div className="space-y-4"><Skeleton className="h-12 rounded-[14px]" /><Skeleton className="h-28 rounded-[14px]" /><Skeleton className="h-48 rounded-[14px]" /></div>;
  if (isError) return <p className="text-[14px] text-destructive" data-testid="proposal-error">{errorMessage(error)}</p>;
  const lim = data.limits;
  const rightsMissing = form.images.some((im) => !im.rights_confirmed);
  const empty = !form.summary && !form.description && !form.description_html && !form.external_url && !form.contact_email && !form.contact_phone && !form.images.length;
  const tooLong = form.summary.length > lim.summary || form.description.length > lim.description || form.note.length > lim.note;

  return (
    <div className="space-y-6" data-testid="proposal-editor">
      <StatusBanner pending={data.pending} last={data.last} />
      <div className="grid gap-6 lg:grid-cols-12 lg:gap-10">
        <div className="space-y-6 lg:col-span-7">
          <Field label="Ingress" hint="Éi til tre setningar som står øvst på arrangementssida." count={{ n: form.summary.length, max: lim.summary }} testId="proposal-summary-field">
            <Textarea rows={2} value={form.summary} onChange={set("summary")} maxLength={lim.summary + 50} className="bg-card" placeholder="T.d. «Eit gratis foredrag om KI, arbeidslivet og kva teknologien kan gjere for di bedrift.»" data-testid="organizer-ingress-input" />
          </Field>
          <Field label="Omtale" hint="Fortel kva publikum får oppleve – med mellomtitlar, feit skrift, lister og lenkjer om du vil." count={{ n: form.description.length, max: lim.description }} testId="proposal-description-field">
            <RichTextLite value={form.description_html} plain={form.description} onChange={(html, text) => setForm((f) => ({ ...f, description_html: html, description: html ? text : "" }))} placeholder="Kva får publikum oppleve?" minHeight={240} testId="organizer-description-editor" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Lenkje til meir informasjon" hint="Eiga nettside eller ekstern påmelding (https://…)" testId="proposal-link-field">
              <Input value={form.external_url} onChange={set("external_url")} placeholder="https://" inputMode="url" className="h-10 bg-card" data-testid="organizer-link-input" />
            </Field>
            <Field label="Kontakt for publikum" hint="Vert synleg på arrangementssida." testId="proposal-contact-field">
              <div className="space-y-2">
                <Input value={form.contact_email} onChange={set("contact_email")} placeholder="e-post" inputMode="email" className="h-10 bg-card" data-testid="organizer-contact-email-input" />
                <Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="telefon" inputMode="tel" className="h-10 bg-card" data-testid="organizer-contact-phone-input" />
              </div>
            </Field>
          </div>
        </div>
        <div className="space-y-6 lg:col-span-5">
          <Field label={`Bilete (inntil ${lim.images})`} hint="Plakatar vert viste heile; foto kan fylle ramma – vel under «Vising». Skriv kven som har teke biletet, og stadfest at de har rett til å bruke det." testId="proposal-images-field">
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1" data-testid="proposal-images">
              {form.images.map((im, i) => <ImageTile key={im.id || im.url} im={im} testId={`proposal-image-${i}`} onChange={(v) => setForm((f) => ({ ...f, images: f.images.map((x, j) => (j === i ? v : x)) }))} onRemove={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} />)}
              {form.images.length < lim.images && (
                <li>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-border bg-card text-[13.5px] font-medium text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground disabled:opacity-60" data-testid="organizer-image-upload">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />} {uploading ? "Lastar opp …" : "Legg til bilete"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="organizer-image-file-input" />
                </li>
              )}
            </ul>
          </Field>
          <Field label="Melding til styret (valfritt)" count={{ n: form.note.length, max: lim.note }} testId="proposal-note-field">
            <Textarea rows={2} value={form.note} onChange={set("note")} maxLength={lim.note + 20} className="bg-card" placeholder="T.d. kva som er endra sidan sist" data-testid="organizer-note-input" />
          </Field>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <p className="text-[13px] text-muted-foreground" data-testid="proposal-footnote">{rightsMissing ? "Stadfest rettane for kvart bilete før du sender." : "Styret ser over og publiserer. Tittel, dato, rom og pris endrar styret."}</p>
        <div className="flex flex-wrap gap-2">
          {data.pending && <Button variant="outline" className="h-11 bg-transparent" onClick={() => setWithdrawOpen(true)} data-testid="organizer-withdraw-btn"><Undo2 className="h-4 w-4" /> Trekk forslaget</Button>}
          <Button className="h-11" onClick={() => submit.mutate()} disabled={submit.isPending || uploading || empty || rightsMissing || tooLong} data-testid="organizer-submit-proposal-btn">
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {data.pending ? "Oppdater forslaget" : "Send til styret"}
          </Button>
        </div>
      </div>
      <ConfirmDialog open={withdrawOpen} onOpenChange={setWithdrawOpen} title="Trekkje forslaget?" text="Forslaget vert fjerna frå styret si liste. Det som ligg på nettsida i dag, vert ikkje endra." confirmLabel="Trekk forslaget" loading={withdraw.isPending} onConfirm={() => withdraw.mutate()} testId="organizer-withdraw-dialog" />
    </div>
  );
};
